import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CartPanelItemTileLayout from './CartPanelItemTileLayout'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onSetDates: () => void
  onChangeRoom: (item: CartPanelAccommodationItem) => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelNeedsDatesTile({
  item,
  onToggleInclude,
  onSetDates,
  onChangeRoom,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()

  const handleChangeRoom = useCallback(() => {
    onChangeRoom(item)
  }, [onChangeRoom, item])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const setDatesLabel = intl.formatMessage({
    id: 'hqRyBjYFB4btbjAKSi2i',
    defaultMessage: 'Set dates to checkout',
    description: 'Attention indicator prompting the user to set trip dates for a hotel in the cart panel',
  })

  const changeRoomLabel = intl.formatMessage({
    id: 'BrfTYdJonj0A3hETLQR8',
    defaultMessage: 'Change room',
    description: 'Link to change the selected room for a hotel in the cart panel',
  })

  const deleteLabel = intl.formatMessage({
    id: 'jDEZjhV3eo-cnxIBHOtR',
    defaultMessage: 'Delete',
    description: 'Button to permanently delete an item from the trip planner cart panel',
  })

  const defaultRoomLabel = intl.formatMessage({
    id: 'm1vHQfyx83yPvfgh-Ma4',
    defaultMessage: 'Default room picked for you',
    description: 'Label indicating the room was auto-selected by the system in the cart panel',
  })

  const content = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading5">{item.name}</Heading>
      {item.roomName && <BodyText variant="medium">{item.roomName}</BodyText>}
      {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
      {item.packageAutoSelected && (
        <HighlightLabel variant="primaryLight" startIcon={<SolidSparklesIcon />}>
          {defaultRoomLabel}
        </HighlightLabel>
      )}
    </Group>
  )

  const attention = (
    <MessagingLabel variant="warningLight">
      {setDatesLabel}
    </MessagingLabel>
  )

  const actions = (
    <Group direction="horizontal" gap={8}>
      <TextButton kind="tertiary" size="small" onClick={onSetDates} disabled={isStreaming}>
        {intl.formatMessage({
          id: 'BicBNDg-DjRAqHnDPAGo',
          defaultMessage: 'Set dates',
          description: 'Action button to set trip dates from the cart panel',
        })}
      </TextButton>
      <TextButton kind="tertiary" size="small" onClick={handleChangeRoom} disabled={isStreaming}>
        {changeRoomLabel}
      </TextButton>
      <TextButton kind="tertiary" size="small" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
        {deleteLabel}
      </TextButton>
    </Group>
  )

  const price = item.pricing.price ? (
    <PriceRowPrice
      price={item.pricing.price}
      priceFormat="preciseDollar"
      saleUnit=""
      size="S"
    />
  ) : undefined

  return (
    <CartPanelItemTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      content={content}
      attention={attention}
      actions={actions}
      price={price}
    />
  )
}

export default CartPanelNeedsDatesTile
